/**
 * SONAUTO — locale-aware formatting for the customer surface.
 *
 * =============================================================================
 * !!! CRITICAL RULE — LOCALE IS NOT CURRENCY !!!
 * =============================================================================
 * Changing the DISPLAY LOCALE must NEVER change the CURRENCY.
 *
 * A Russian guest at a Hanoi branch switching the app to Русский still pays in
 * VND. There is no exchange rate, no conversion, no "friendly" re-denomination
 * anywhere in this product. `formatPriceForCustomer` therefore takes the
 * branch's configured currency as a REQUIRED argument and never derives one
 * from the locale — the locale only decides digit grouping, decimal separator
 * and symbol placement.
 *
 * If you ever find yourself writing `locale === 'ru-RU' ? 'RUB' : ...`, stop:
 * that is a financial defect, not a formatting improvement. The amount is an
 * integer count of minor units (see @sonauto/utilities money.ts) and the
 * exponent belongs to the currency, not to the locale.
 * =============================================================================
 *
 * All formatters are Intl-based (available in Workers, browsers and Node 20+),
 * memoised per (locale, options) because constructing an Intl formatter is the
 * expensive part, and total: an unknown locale or currency degrades to a plain
 * rendering instead of throwing at a guest.
 */

import { DEFAULT_LOCALE, getLocaleInfoOrDefault, languageOf } from './locales.js';

const DEFAULT_TIME_ZONE = 'Asia/Ho_Chi_Minh';

const DATE_TIME_CACHE = new Map<string, Intl.DateTimeFormat>();
const NUMBER_CACHE = new Map<string, Intl.NumberFormat>();

function dateTimeFormatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat | null {
  const key = `${locale}|${JSON.stringify(options)}`;
  const cached = DATE_TIME_CACHE.get(key);
  if (cached !== undefined) return cached;
  try {
    const created = new Intl.DateTimeFormat(locale, options);
    DATE_TIME_CACHE.set(key, created);
    return created;
  } catch {
    return null;
  }
}

function numberFormatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat | null {
  const key = `${locale}|${JSON.stringify(options)}`;
  const cached = NUMBER_CACHE.get(key);
  if (cached !== undefined) return cached;
  try {
    const created = new Intl.NumberFormat(locale, options);
    NUMBER_CACHE.set(key, created);
    return created;
  } catch {
    return null;
  }
}

/** Normalise to a locale Intl will accept; unknown tags fall back to vi-VN. */
function safeLocale(locale: string | null | undefined): string {
  const info = getLocaleInfoOrDefault(locale);
  return info.locale;
}

export interface FormatOptions {
  /** IANA timezone; defaults to the Vietnamese branch timezone. */
  readonly timeZone?: string;
}

/** `at` is epoch milliseconds (server clock — see utilities/time.ts). */
export function formatDate(at: number, locale: string = DEFAULT_LOCALE, options: FormatOptions = {}): string {
  const fmt = dateTimeFormatter(safeLocale(locale), {
    timeZone: options.timeZone ?? DEFAULT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt === null ? new Date(at).toISOString().slice(0, 10) : fmt.format(new Date(at));
}

export function formatTime(at: number, locale: string = DEFAULT_LOCALE, options: FormatOptions = {}): string {
  const fmt = dateTimeFormatter(safeLocale(locale), {
    timeZone: options.timeZone ?? DEFAULT_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
  });
  return fmt === null ? new Date(at).toISOString().slice(11, 16) : fmt.format(new Date(at));
}

export function formatDateTime(at: number, locale: string = DEFAULT_LOCALE, options: FormatOptions = {}): string {
  const fmt = dateTimeFormatter(safeLocale(locale), {
    timeZone: options.timeZone ?? DEFAULT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  return fmt === null ? new Date(at).toISOString().replace('T', ' ').slice(0, 16) : fmt.format(new Date(at));
}

export interface NumberFormatOptions {
  readonly minimumFractionDigits?: number;
  readonly maximumFractionDigits?: number;
  readonly useGrouping?: boolean;
}

export function formatNumber(
  value: number,
  locale: string = DEFAULT_LOCALE,
  options: NumberFormatOptions = {},
): string {
  if (!Number.isFinite(value)) return '';
  const fmt = numberFormatter(safeLocale(locale), {
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 3,
    useGrouping: options.useGrouping ?? true,
  });
  return fmt === null ? String(value) : fmt.format(value);
}

export function formatPercent(basisPoints: number, locale: string = DEFAULT_LOCALE): string {
  if (!Number.isFinite(basisPoints)) return '';
  const fmt = numberFormatter(safeLocale(locale), {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  const ratio = basisPoints / 10_000;
  return fmt === null ? `${basisPoints / 100}%` : fmt.format(ratio);
}

// ---------------------------------------------------------------------------
// Duration
// ---------------------------------------------------------------------------

interface DurationWords {
  readonly minute: string;
  readonly hour: string;
  readonly day: string;
  readonly lessThanAMinute: string;
}

/**
 * Short duration words per LANGUAGE (not per locale — `zh-CN` and a future
 * `zh-TW` share these).
 *
 * A newly enabled catalogue locale whose language has no entry here degrades
 * to English rather than blocking the locale from being enabled, which keeps
 * the "add a locale = one catalogue entry" contract intact. Adding better
 * words here later is a pure improvement, never a prerequisite. (A tag that is
 * not in the catalogue at all never reaches this map: `safeLocale` resolves it
 * to the source locale first, so it renders Vietnamese.)
 */
const DURATION_WORDS: Readonly<Record<string, DurationWords>> = {
  vi: { minute: 'phút', hour: 'giờ', day: 'ngày', lessThanAMinute: 'dưới 1 phút' },
  en: { minute: 'min', hour: 'h', day: 'd', lessThanAMinute: 'less than a minute' },
  zh: { minute: '分钟', hour: '小时', day: '天', lessThanAMinute: '不到 1 分钟' },
  ru: { minute: 'мин', hour: 'ч', day: 'дн', lessThanAMinute: 'менее минуты' },
};

function durationWords(locale: string): DurationWords {
  const lang = languageOf(safeLocale(locale));
  return DURATION_WORDS[lang] ?? DURATION_WORDS['en'] ?? DURATION_WORDS['vi']!;
}

/**
 * Human duration from MINUTES — the unit every kitchen SLA and "món của bạn
 * sẵn sàng sau ~15 phút" estimate uses.
 *
 * Vietnamese-friendly by design: `95` → `1 giờ 35 phút`, not `1.58 giờ`.
 * Negative or non-finite input renders as the "less than a minute" phrase
 * rather than a minus sign the guest cannot interpret.
 */
export function formatDuration(minutes: number, locale: string = DEFAULT_LOCALE): string {
  const words = durationWords(locale);
  if (!Number.isFinite(minutes) || minutes < 1) return words.lessThanAMinute;

  const total = Math.floor(minutes);
  const days = Math.floor(total / (60 * 24));
  const hours = Math.floor((total % (60 * 24)) / 60);
  const mins = total % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days} ${words.day}`);
  if (hours > 0) parts.push(`${hours} ${words.hour}`);
  if (mins > 0 && days === 0) parts.push(`${mins} ${words.minute}`);
  return parts.length === 0 ? words.lessThanAMinute : parts.join(' ');
}

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/**
 * Render a price for the guest.
 *
 * @param amountMinor integer count of MINOR units (25000 VND ⇒ 25000; $2.50 ⇒ 250)
 * @param currency    the BRANCH's configured ISO-4217 code. REQUIRED. Never
 *                    inferred from `locale`, never converted.
 * @param exponent    minor-unit exponent of that currency (VND 0, USD 2)
 * @param locale      display locale — affects grouping/separators ONLY
 *
 * `25.000 ₫` in vi-VN and `25 000 ₫` in ru-RU are the SAME amount in the SAME
 * currency. That is the entire scope of what the locale is allowed to change.
 */
export function formatPriceForCustomer(
  amountMinor: number,
  currency: string,
  exponent: number,
  locale: string = DEFAULT_LOCALE,
): string {
  if (!Number.isFinite(amountMinor)) return '';
  const safeExponent = Number.isInteger(exponent) && exponent >= 0 && exponent <= 4 ? exponent : 0;
  const major = amountMinor / 10 ** safeExponent;

  const fmt = numberFormatter(safeLocale(locale), {
    style: 'currency',
    currency,
    minimumFractionDigits: safeExponent,
    maximumFractionDigits: safeExponent,
  });
  if (fmt !== null) return fmt.format(major);

  // Unknown/invalid currency code: still show the number and the raw code
  // rather than throwing. The CURRENCY is never silently swapped.
  const plain = numberFormatter(safeLocale(locale), {
    minimumFractionDigits: safeExponent,
    maximumFractionDigits: safeExponent,
  });
  const digits = plain === null ? major.toFixed(safeExponent) : plain.format(major);
  return `${digits} ${currency}`;
}

/** Price range for an item with variants, e.g. `25.000 ₫ – 45.000 ₫`. */
export function formatPriceRangeForCustomer(
  minMinor: number,
  maxMinor: number,
  currency: string,
  exponent: number,
  locale: string = DEFAULT_LOCALE,
): string {
  const lo = formatPriceForCustomer(minMinor, currency, exponent, locale);
  if (minMinor === maxMinor) return lo;
  const hi = formatPriceForCustomer(maxMinor, currency, exponent, locale);
  return `${lo} – ${hi}`;
}
